"use client"

import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { Button, ArrowRightIcon } from "@/components/ui/button"
import { BookingCta } from "@/components/ui/booking-cta"
import { membershipBookingUrl } from "@/lib/booking"
import { withQuizAttribution } from "@/lib/quiz/booking-source"
import { CheckCircleIcon, XCircleIcon } from "@/components/ui/icons"
import { MembershipPillIcon } from "@/components/ui/new-membership-pill"
import {
  WhyEyebrow,
  AddOnRow,
  type RecommendationAddOn,
} from "./recommendation-card"
import { cn } from "@/lib/utils"
import type { AddOnId } from "@/lib/quiz/types"

/* Shared card shadow — copied verbatim from blood-membership-comparison.tsx so
   the two cards read as one matched pair. */
const CARD_SHADOW = "shadow-[0px_3px_16px_0px_rgba(106,133,161,0.05)]"

/* Comparison checklist. The first three rows are shared with the one-off; the
   last three are membership-only (soft X on the one-off). Order + copy mirror
   BENEFIT_ROWS in blood-membership-comparison.tsx. */
const ROW_KEYS = [
  { key: "test", inOneOff: true },
  { key: "report", inOneOff: true },
  { key: "addLater", inOneOff: true },
  { key: "fourTests", inOneOff: false },
  { key: "support", inOneOff: false },
  { key: "memberPricing", inOneOff: false },
] as const

export interface MembershipSideBySideProps {
  currency: string
  membershipPrice: string
  membershipAddOnsTotal?: string
  oneOffPrice: string
  oneOffAddOnsTotal?: string
  hasPaidAddOns: boolean
  oneOffCtaHref: string
  addOns: RecommendationAddOn[]
  addOnsSummary?: string
  onRemoveAddOn: (id: AddOnId) => void
}

function ComparisonRow({
  label,
  included,
}: {
  label: string
  included: boolean
}) {
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      {included ? (
        <CheckCircleIcon size={20} className="flex-shrink-0" />
      ) : (
        <XCircleIcon size={20} className="flex-shrink-0 text-neutral-300" />
      )}
      <span
        className={cn(
          "type-body-md",
          included ? "text-neutral-900" : "text-neutral-400"
        )}
      >
        {label}
      </span>
    </div>
  )
}

export function QuizMembershipSideBySide({
  currency,
  membershipPrice,
  membershipAddOnsTotal,
  oneOffPrice,
  oneOffAddOnsTotal,
  hasPaidAddOns,
  oneOffCtaHref,
  addOns,
  addOnsSummary,
  onRemoveAddOn,
}: MembershipSideBySideProps) {
  const t = useTranslations("quiz.result.membership")
  const locale = useLocale()
  return (
    <div className="relative left-1/2 w-[min(100vw-2rem,1040px)] -translate-x-1/2">
      {/* Both cards are row-span-3 grid-rows-subgrid so header / CTA / checklist
          sit on the same baseline across the two columns. */}
      <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
        {/* ── Membership (recommended) ── */}
        <div
          className={cn(
            "pill-magic-stroke pill-magic-stroke-always relative row-span-3 grid grid-rows-subgrid gap-y-6 rounded-[20px] bg-gradient-to-br from-[#FCFAFE] via-[#FAFBFE] to-[#F7FBFF] p-5 md:p-8", // ds-color-ignore: gradient stop
            CARD_SHADOW
          )}
        >
          <div className="absolute top-0 right-12 z-10 -translate-y-[calc(50%+2px)]">
            <span className="badge-subtle-hue-drift text-light-blue-900 type-body-sm inline-block rounded-full px-5 py-1 font-medium normal-case">
              {t("badge")}
            </span>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <MembershipPillIcon size={40} />
              <h3 className="type-headline-md text-neutral-900">
                {t("membershipTitle")}
              </h3>
            </div>
            <p className="type-body-md mt-2 text-neutral-500">
              {t("membershipEyebrow")}
            </p>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="type-body-md text-neutral-500">{currency}</span>
              <span className="type-display-sm text-neutral-900">
                {membershipPrice}
              </span>
              <span className="type-body-md text-neutral-500">
                {t("perYear")}
              </span>
            </div>
            {membershipAddOnsTotal && (
              <p className="type-body-sm mt-1 text-neutral-500">
                {t("plusAddOns", { currency, amount: membershipAddOnsTotal })}
              </p>
            )}
            <div className="mt-3">
              <WhyEyebrow>{t("whyLine")}</WhyEyebrow>
            </div>
          </div>
          <div>
            <BookingCta
              forMembership
              overrideHref={withQuizAttribution(membershipBookingUrl(locale))}
              trackingLabel="quiz_result_membership_cta"
              trackingLocation="quiz-result"
              className="block"
            >
              <Button
                variant="primary"
                className="group w-full"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                {t("membershipCta")}
              </Button>
            </BookingCta>
          </div>
          <div className="flex flex-col">
            {ROW_KEYS.map((r) => (
              <ComparisonRow
                key={r.key}
                label={t(`rows.${r.key}` as never)}
                included
              />
            ))}
          </div>
        </div>

        {/* ── One-off ── */}
        <div
          className={cn(
            "row-span-3 grid grid-rows-subgrid gap-y-6 rounded-[20px] bg-white p-5 md:p-8",
            CARD_SHADOW
          )}
        >
          <div className="flex flex-col">
            <h3 className="type-headline-md text-neutral-900">
              {t("oneOffTitle")}
            </h3>
            <p className="type-body-md mt-2 text-neutral-500">
              {t("oneOffEyebrow")}
            </p>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="type-body-md text-neutral-500">{currency}</span>
              <span className="type-display-sm text-neutral-900">
                {oneOffPrice}
              </span>
              <span className="type-body-md text-neutral-500">
                {t("oneTime")}
              </span>
            </div>
            {oneOffAddOnsTotal && (
              <p className="type-body-sm mt-1 text-neutral-500">
                {t("plusAddOns", { currency, amount: oneOffAddOnsTotal })}
              </p>
            )}
          </div>
          <div>
            <BookingCta
              overrideHref={oneOffCtaHref}
              trackingLabel="quiz_result_oneoff_cta"
              trackingLocation="quiz-result"
              className="block"
            >
              <Button
                variant="outline"
                className="group w-full"
                iconRight={
                  <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
                }
              >
                {t("oneOffCta")}
              </Button>
            </BookingCta>
          </div>
          <div className="flex flex-col">
            {ROW_KEYS.map((r) => (
              <ComparisonRow
                key={r.key}
                label={t(`rows.${r.key}` as never)}
                included={r.inOneOff}
              />
            ))}
          </div>
        </div>
      </div>

      {addOns.length > 0 && (
        <div className="mt-8">
          <p className="type-label-lg text-neutral-500">
            {hasPaidAddOns ? t("addOnsLabel") : t("addOnsLabelPlain")}
          </p>
          {addOnsSummary && (
            <div className="mt-2 mb-3">
              <WhyEyebrow>{addOnsSummary}</WhyEyebrow>
            </div>
          )}
          <ul className="mt-3 space-y-2">
            {addOns.map((addOn) => (
              <AddOnRow key={addOn.id} addOn={addOn} onRemove={onRemoveAddOn} />
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
